<template>
  <div class="reset-password-page">
    <div class="reset-password-container">
      <div class="reset-password-box">
      <div class="logo-section">
        <img :src="require('@/assets/img/OVB_logo_gray.svg')" alt="OVB" class="logo" />
        <h1 class="title">Set new password</h1>
        <p v-if="!completed && !invalidToken" class="subtitle">
          Enter your new password below.
        </p>
      </div>

      <div v-if="loggingOut" class="info-box">
        <h2>Preparing Password Reset</h2>
        <p>
          Logging you out of your current session...
        </p>
        <div class="spinner"></div>
      </div>

      <div v-else-if="invalidToken" class="error-box">
        <h2>Invalid or Expired Link</h2>
        <p>
          This password reset link is invalid or has expired.
        </p>
        <router-link to="/forgot-password" class="continue-button-link">
          Request New Reset Link
        </router-link>
      </div>

      <div v-else-if="completed" class="success-message">
        <h2>Password Reset Successfully</h2>
        <p>
          Your password has been updated. You can now log in with your new password.
        </p>
        <router-link to="/login" class="continue-button-link">
          Continue to Login
        </router-link>
      </div>

      <form v-else @submit.prevent="handleSubmit" class="reset-password-form">
        <div class="input-wrapper">
          <label for="password" class="input-label">
            New Password
            <span class="required" aria-hidden="true">*</span>
          </label>
          <div class="password-field-wrapper">
            <input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="input-field"
              required
              autocomplete="new-password"
              autocapitalize="none"
              spellcheck="false"
              :disabled="loading"
              minlength="8"
              autofocus
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <span class="password-icon">{{ showPassword ? '👁️' : '👁️‍🗨️' }}</span>
            </button>
          </div>
          <p class="hint">Minimum 8 characters</p>
        </div>

        <div class="input-wrapper">
          <label for="confirmPassword" class="input-label">
            Confirm New Password
            <span class="required" aria-hidden="true">*</span>
          </label>
          <div class="password-field-wrapper">
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="input-field"
              required
              autocomplete="new-password"
              autocapitalize="none"
              spellcheck="false"
              :disabled="loading"
              minlength="8"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showConfirmPassword = !showConfirmPassword"
              :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
            >
              <span class="password-icon">{{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}</span>
            </button>
          </div>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button type="submit" :disabled="loading || !passwordsMatch" class="continue-button">
          {{ loading ? 'Resetting...' : 'Reset Password' }}
        </button>
      </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ResetPassword',
  data () {
    return {
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      error: null,
      completed: false,
      invalidToken: false,
      token: null,
      loggingOut: false
    }
  },
  computed: {
    passwordsMatch () {
      return this.password && this.confirmPassword && this.password === this.confirmPassword
    }
  },
  async mounted () {
    // Get token from URL query parameter
    this.token = this.$route.query.token
    if (!this.token) {
      this.invalidToken = true
      return
    }

    // If user is logged in, log them out silently without redirecting
    // Password reset should only be done when NOT logged in
    if (this.$store.state.user.isLoggedIn) {
      this.loggingOut = true
      try {
        // Clear session on backend
        await axios.get('/logout', { withCredentials: true }).catch(() => {
          // Ignore errors
        })

        // Clear local state
        this.$store.dispatch('user/logout').catch(() => {})
        this.$store.dispatch('app/logout').catch(() => {})

        // Wait a moment for cleanup
        await new Promise(resolve => setTimeout(resolve, 500))
      } catch (e) {
        console.error('Error logging out:', e)
      } finally {
        this.loggingOut = false
      }
    }
  },
  methods: {
    async handleSubmit () {
      if (!this.passwordsMatch) {
        this.error = 'Passwords do not match'
        return
      }

      if (this.password.length < 8) {
        this.error = 'Password must be at least 8 characters'
        return
      }

      this.loading = true
      this.error = null

      try {
        await axios.post('/api/auth/reset-password', {
          token: this.token,
          password: this.password
        }, {
          withCredentials: true
        })

        this.completed = true
      } catch (err) {
        console.error('Reset password error:', err)
        if (err.response?.status === 400 || err.response?.status === 401) {
          this.invalidToken = true
        } else {
          this.error = err.response?.data?.error || 'Failed to reset password. Please try again.'
        }
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Auth0-inspired design - blue background, white box, no header */
.reset-password-page {
  min-height: 100vh;
  background: #000000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.reset-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.reset-password-box {
  background: #ffffff;
  padding: 48px 40px;
  border-radius: 5px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  width: 100%;
  max-width: 450px;
}

.logo-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo {
  width: auto;
  height: auto;
  max-height: 50px;
  max-width: 200px;
  margin-bottom: 24px;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  margin: 0 0 16px 0;
  line-height: 1.3;
}

.subtitle {
  font-size: 14px;
  color: #65676e;
  margin: 0;
  line-height: 1.6;
}

.reset-password-form {
  margin-top: 32px;
}

.input-wrapper {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 8px;
  line-height: 1.5;
}

.required {
  color: #d00e17;
  margin-left: 2px;
}

.input-field {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #c9cccf;
  border-radius: 3px;
  font-size: 15px;
  line-height: 1.5;
  color: #000;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-field:hover {
  border-color: #b0b4b8;
}

.input-field:focus {
  outline: none;
  border-color: #b0b4b8;
  box-shadow: none;
}

.input-field:disabled {
  background: #f9f9f9;
  cursor: not-allowed;
  opacity: 0.6;
}

.password-field-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  font-size: 18px;
  line-height: 1;
  color: #65676e;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: #000;
}

.password-icon {
  display: block;
}

.hint {
  font-size: 13px;
  color: #9196a1;
  margin: 6px 0 0 0;
}

.error-message {
  background: #fee;
  color: #d00e17;
  padding: 12px 16px;
  border-radius: 3px;
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid #fcc;
}

.info-box {
  text-align: center;
}

.info-box h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0a8852;
  margin: 0 0 16px 0;
}

.info-box p {
  font-size: 14px;
  color: #65676e;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #ef4356;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-box {
  text-align: center;
}

.error-box h2 {
  font-size: 18px;
  font-weight: 600;
  color: #d00e17;
  margin: 0 0 16px 0;
}

.error-box p {
  font-size: 14px;
  color: #65676e;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.success-message {
  text-align: center;
}

.success-message h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0a8852;
  margin: 0 0 16px 0;
}

.success-message p {
  font-size: 14px;
  color: #65676e;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.continue-button {
  width: 100%;
  padding: 12px;
  background: #ef4356;
  color: #ffffff;
  border: none;
  border-radius: 3px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  line-height: 1.5;
}

.continue-button:hover:not(:disabled) {
  background: #d63848;
}

.continue-button:disabled {
  background: #f9b2ba;
  cursor: not-allowed;
}

.continue-button-link {
  display: inline-block;
  padding: 12px 24px;
  background: #ef4356;
  color: #ffffff;
  border: none;
  border-radius: 3px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
  line-height: 1.5;
}

.continue-button-link:hover {
  background: #d63848;
}
</style>
